import React, { useState } from 'react';
import { 
  EnvelopeIcon, 
  CheckCircleIcon, 
  PaperAirplaneIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { AppSettings, EmailTemplate } from '../../types';
import { updateSettings } from '../../services/settingsService';
import { sendTestEmail } from '../../services/emailService';

interface Props {
  settings: AppSettings;
  onUpdate: (newSettings: AppSettings) => void;
}

const EmailTemplateEditor: React.FC<Props> = ({ settings, onUpdate }) => {
  const [activeTemplate, setActiveTemplate] = useState<keyof NonNullable<AppSettings['emailTemplates']>>('applicationReceived');
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const templates = settings.emailTemplates || {
    applicationReceived: { subject: '', title: '', message: '' },
    applicationVerified: { subject: '', title: '', message: '' },
    applicationRejected: { subject: '', title: '', message: '' },
    selectionConfirmation: { subject: '', title: '', message: '' }
  };

  const currentTemplate = templates[activeTemplate];

  const handleTemplateChange = (field: keyof EmailTemplate, value: string) => {
    const newTemplates = {
      ...templates,
      [activeTemplate]: {
        ...currentTemplate,
        [field]: value
      }
    };
    onUpdate({
      ...settings,
      emailTemplates: newTemplates
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      alert('Email templates saved successfully!');
    } catch (error) {
      console.error('Error saving templates:', error);
      alert('Failed to save templates.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    const email = window.prompt('Enter email address to send test to:', settings.senderEmail);
    if (!email) return;

    setSendingTest(true);
    try {
      const success = await sendTestEmail(activeTemplate, {
        to_email: email,
        template_title: currentTemplate.title,
        template_message: currentTemplate.message,
        subject: currentTemplate.subject
      });
      if (success) {
        alert('Test email sent successfully!');
      } else {
        alert('Failed to send test email. Check console for details.');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Error sending test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const templateOptions = [
    { id: 'applicationReceived', name: 'Application Received', icon: DocumentTextIcon },
    { id: 'applicationVerified', name: 'Application Verified', icon: CheckCircleIcon },
    { id: 'applicationRejected', name: 'Application Rejected', icon: EnvelopeIcon },
    { id: 'selectionConfirmation', name: 'Selection Confirmation', icon: PaperAirplaneIcon }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
          <p className="text-sm text-gray-500">Customize the automated emails sent to applicants</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSendTest}
            disabled={sendingTest}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            <span>{sendingTest ? 'Sending...' : 'Send Test'}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-primary-700 transition-colors shadow-sm"
          >
            <CheckCircleIcon className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save All Templates'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 space-y-2">
          {templateOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                onClick={() => setActiveTemplate(option.id as any)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTemplate === option.id
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{option.name}</span>
              </button>
            );
          })}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-3">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={currentTemplate.subject}
                  onChange={(e) => handleTemplateChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter email subject line"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Title (Heading)
                </label>
                <input
                  type="text"
                  value={currentTemplate.title}
                  onChange={(e) => handleTemplateChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter the main heading inside the email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Message
                </label>
                <textarea
                  rows={6}
                  value={currentTemplate.message}
                  onChange={(e) => handleTemplateChange('message', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter the main content of the email"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Note: Common details like Registration ID, Date, and Footer are automatically added.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {['{{id}}', '{{name}}', '{{firstName}}', '{{lastName}}', '{{date}}', '{{securityCode}}', '{{trainingDate}}', '{{venue}}'].map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview Card */}
            <div className="mt-8">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Live Preview</h3>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-primary-900 p-4 text-center">
                  <h1 className="text-white font-bold tracking-tight">KREED ODISHA</h1>
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{currentTemplate.title || 'Template Title'}</h2>
                  <p className="text-gray-600 leading-relaxed">
                    {currentTemplate.message || 'Template message content will appear here...'}
                  </p>
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-400 text-center">
                      This is an automated message from the Kreed Odisha Platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
